import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, LogOut } from "lucide-react";
import { CartaoRecebido, msgErro } from "@/components/colab/ColabRecebidos";
import logoMark from "@/assets/logo-mark.png";

// Lote 161 — acesso do parceiro SEM conta: informa o e-mail do convite, digita o código de uso único que chegou nele e vê só o que foi
// compartilhado com aquele e-mail. Sem CPF, OAB, Termo ou código de cadastro. O servidor responde sempre igual ao pedido de código
// (não revela se o e-mail é o do convite). A sessão dura algumas horas e vale só para esta pessoa.
export default function ExternoConvite() {
  const { shareId } = useParams();
  const [fase, setFase] = useState("carregando");        // carregando | email | codigo | logado
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [aviso, setAviso] = useState("");
  const [erro, setErro] = useState("");
  const [busy, setBusy] = useState(false);
  const [lista, setLista] = useState(null);
  const [erroLista, setErroLista] = useState(false);
  const [aberto, setAberto] = useState(shareId);

  const carregar = useCallback(async () => {
    try { setLista((await http.get("/externo/compartilhamentos")).data); setErroLista(false); }
    catch (e) { if (e?.response?.status === 401) setFase("email"); else setErroLista(true); }
  }, []);
  useEffect(() => {
    http.get("/externo/eu").then((r) => { setEmail(r.data.email); setFase("logado"); carregar(); }).catch(() => setFase("email"));
  }, [carregar]);

  const pedir = async () => {
    setBusy(true); setErro("");
    try { const r = await http.post("/externo/codigo", { share_id: shareId, email }); setAviso(r.data.mensagem); setFase("codigo"); setCodigo(""); }
    catch (e) { setErro(msgErro(e, "Não foi possível pedir o código agora.")); } finally { setBusy(false); }
  };
  const entrar = async () => {
    setBusy(true); setErro("");
    try { const r = await http.post("/externo/entrar", { share_id: shareId, email, codigo }); setEmail(r.data.email); setFase("logado"); await carregar(); }
    catch (e) { setErro(msgErro(e, "Código inválido ou expirado.")); } finally { setBusy(false); }
  };
  const sair = async () => { try { await http.post("/externo/sair"); } catch { /* a sessão expira sozinha */ } setLista(null); setFase("email"); setCodigo(""); setAviso(""); };

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-4" data-testid="externo-pagina">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <img src={logoMark} alt="" className="w-8 h-8 rounded-md" />
          <span className="font-display font-bold text-stone-900 text-lg"><span className="text-gold-600">IA</span>pauta</span>
        </div>

        {fase === "carregando" && <div className="p-8 text-center text-stone-500">Carregando…</div>}

        {(fase === "email" || fase === "codigo") && (
          <div className="bg-white border border-stone-200 rounded-md p-6 md:p-8">
            <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} /> Convite de compartilhamento</div>
            <h1 className="font-display text-2xl font-bold text-stone-900 mb-2">Abra o que foi compartilhado com você</h1>
            <p className="text-sm text-stone-600 mb-5">Informe o e-mail em que recebeu o convite. Enviaremos um código de uso único para ele — não é preciso criar conta.</p>
            {fase === "email" ? (
              <form onSubmit={(e) => { e.preventDefault(); pedir(); }} className="space-y-3">
                <div><Label htmlFor="ex-email">Seu e-mail</Label><Input id="ex-email" data-testid="externo-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                <Button type="submit" data-testid="externo-pedir" disabled={busy || !email.includes("@")} className="w-full bg-stone-900 hover:bg-stone-800 text-white">{busy ? "Enviando…" : "Enviar código"}</Button>
              </form>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); entrar(); }} className="space-y-3">
                <p data-testid="externo-aviso" className="text-sm text-stone-700 rounded-md bg-stone-50 border border-stone-200 p-3">{aviso}</p>
                <div><Label htmlFor="ex-cod">Código de 6 dígitos</Label><Input id="ex-cod" data-testid="externo-codigo" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))} className="tracking-[0.4em] text-lg" /></div>
                <Button type="submit" data-testid="externo-entrar" disabled={busy || codigo.length !== 6} className="w-full bg-stone-900 hover:bg-stone-800 text-white">{busy ? "Entrando…" : "Entrar"}</Button>
                <button type="button" data-testid="externo-novo-codigo" className="text-xs underline text-stone-600" onClick={() => { setFase("email"); setErro(""); }}>Voltar e pedir outro código</button>
              </form>
            )}
            {erro && <div role="alert" data-testid="externo-erro" className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{erro}</div>}
            <p className="text-xs text-stone-500 mt-5">Já tem conta no IApauta? <Link data-testid="externo-tenho-conta" to={`/compartilhamentos?aba=recebidos&abrir=${shareId}`} className="underline">Entre com ela</Link> e o convite aparece em “Recebidos”.</p>
          </div>
        )}

        {fase === "logado" && (
          <div data-testid="externo-logado">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="text-sm text-stone-600 min-w-0 truncate">Acesso como <b data-testid="externo-email-logado">{email}</b></div>
              <Button type="button" variant="outline" size="sm" data-testid="externo-sair" onClick={sair} className="gap-1.5 shrink-0"><LogOut className="w-3.5 h-3.5" strokeWidth={1.75} /> Sair</Button>
            </div>
            {erroLista ? (
              <div data-testid="externo-erro-lista" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">Não foi possível carregar. <button type="button" className="underline font-semibold" onClick={carregar}>Tentar de novo</button></div>
            ) : lista === null ? (
              <div className="p-8 text-center text-stone-500">Carregando…</div>
            ) : (
              <>
                {!lista.some((x) => x.share_id === shareId) && (
                  <div data-testid="externo-nao-encontrado" className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Este convite não está disponível para o e-mail {email}. Ele pode ter expirado, ter sido encerrado ou ser de outra pessoa.</div>
                )}
                {lista.length === 0 ? (
                  <div data-testid="externo-vazio" className="bg-white border border-stone-200 rounded-md p-8 text-center text-sm text-stone-500">Nada compartilhado com este e-mail no momento.</div>
                ) : (
                  <ul className="space-y-2.5">
                    {lista.map((r) => <CartaoRecebido key={r.share_id} r={r} base="/externo/compartilhamentos" convidado aberto={aberto === r.share_id} onAlternar={() => setAberto(aberto === r.share_id ? null : r.share_id)} onMudou={carregar} />)}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
        <p className="text-center text-xs text-stone-400 mt-6">Recebeu por engano? Basta ignorar. Ninguém acessa nada sem o código enviado ao e-mail do convite.</p>
      </div>
    </div>
  );
}
